"use client";

import { cn } from "@/lib/utils";
import { Sparkles } from "lucide-react";
interface DisplayCardProps {
  className?: string;
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  date?: string;
  iconClassName?: string;
  titleClassName?: string;
  variant?: "default" | "right";
  /** The degree of skew in degrees. Defaults to 8. */
  skewDegree?: number;
}
function DisplayCard({
  className,
  icon = <Sparkles />,
  title = "Featured",
  description = "Discover amazing content",
  date = "Just now",
  iconClassName = "",
  titleClassName = "",
  variant = "default",
  skewDegree = 4
}: DisplayCardProps) {
  const skewDirection = variant === "right" ? `skew-y-[${skewDegree}deg]` : `-skew-y-[${skewDegree}deg]`;
  return <div className={cn(`group relative flex h-36 w-[22rem] ${skewDirection} select-none flex-col justify-between rounded-xl border-2 bg-card px-4 py-3 backdrop-blur-sm transition-all duration-700 after:absolute after:-right-1 after:top-[-5%] after:h-[110%] after:w-[20rem] after:bg-gradient-to-l after:from-background after:to-transparent after:content-[''] hover:border-black/20 hover:bg-card dark:hover:border-white/20 [&>*]:flex [&>*]:items-center [&>*]:gap-2`, className)} data-sentry-component="DisplayCard" data-sentry-source-file="display-cards.tsx">
      <div>
        <span className={cn("relative inline-block rounded-full p-1 opacity-50 transition-opacity duration-700 group-hover:opacity-100", iconClassName)}>
          {icon}
        </span>
        <p className={cn("text-lg font-medium opacity-50 transition-opacity duration-700 group-hover:opacity-100", titleClassName)}>
          {title}
        </p>
      </div>
      <p className="whitespace-nowrap text-lg opacity-50 transition-opacity duration-700 group-hover:opacity-100">
        {description}
      </p>
      <p className="text-muted-foreground opacity-50 transition-opacity duration-700 group-hover:opacity-100">
        {date}
      </p>
    </div>;
}
interface DisplayCardsProps {
  cards?: DisplayCardProps[];
  variant?: "default" | "right";
}
export default function DisplayCards({
  cards,
  variant = "default"
}: DisplayCardsProps) {
  const getDefaultCards = () => {
    if (variant === "right") {
      return [{
        className: "[grid-area:stack] hover:-translate-y-10 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0"
      }, {
        className: "[grid-area:stack] -translate-x-16 translate-y-10 hover:-translate-y-1 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0"
      }, {
        className: "[grid-area:stack] -translate-x-32 translate-y-20 hover:translate-y-10"
      }];
    }
    return [{
      className: "[grid-area:stack] hover:-translate-y-10 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration:700 hover:grayscale-0 before:left-0 before:top-0"
    }, {
      className: "[grid-area:stack] translate-x-16 translate-y-10 hover:-translate-y-1 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0"
    }, {
      className: "[grid-area:stack] translate-x-32 translate-y-20 hover:translate-y-10"
    }];
  };
  const displayCards = cards || getDefaultCards();
  return <div className="grid place-items-center opacity-100 duration-700 animate-in fade-in-0 [grid-template-areas:'stack']" data-sentry-component="DisplayCards" data-sentry-source-file="display-cards.tsx">
      {displayCards.map((cardProps, index) => <DisplayCard key={index} {...cardProps} variant={variant} />)}
    </div>;
}